import React from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Global from '../../../store/global-selectors';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import PropTypes from 'prop-types';

import { emptyObject } from 'ui-components/default-props/empty';

import FormattedCurrencyContainer from '../../../components/formatted-currency/container';
import { SalesByStoreTable } from '../components/sales-by-store-table';
import { SalesByCategoryTable } from '../components/sales-by-category-table';
import { TopFiveProductsByRevenueTable } from '../components/top-five-products-by-revenue-table';
import NumItemsSoldOverTimeGraph from '../components/graphs/num-items-sold-over-time-graph';
import SummaryBar from '../components/summary-bar';
import InsufficientDataBannerMessage from '../components/insufficient-data-banner-message';
import { doubleDash } from '../constants';

import messages from './messages';
import style from './style.module.less';

const renderSummarySection = (customerSummary = emptyObject, defaultCurrency) => {
  const { newCustomers, returnCustomers, totalOrderAmount, totalOrderAmountPrev, avgOrderAmount, totalOrderCount } = customerSummary;
  const dataIsUndefined = newCustomers === undefined;
  const salesVsTimeLastYear = totalOrderAmountPrev > 0 ? totalOrderAmount / totalOrderAmountPrev : 1;

  const formattedData = [
    {
      title: <FormattedMessage {...messages.salesSummary} values={{ currency: defaultCurrency }} />,
      value: dataIsUndefined ? doubleDash : <FormattedCurrencyContainer currencyCode={defaultCurrency} value={totalOrderAmount} />,
    },
    {
      title: <FormattedMessage {...messages.salesVsTimeLastYear} />,
      value:
        dataIsUndefined || !totalOrderAmountPrev ? (
          doubleDash
        ) : (
          <FormattedNumber maximumFractionDigits={0} style='percent' value={salesVsTimeLastYear} />
        ),
    },
    {
      title: <FormattedMessage {...messages.averageOrderValue} values={{ currency: defaultCurrency }} />,
      value: dataIsUndefined ? doubleDash : <FormattedCurrencyContainer currencyCode={defaultCurrency} value={avgOrderAmount} />,
    },
    {
      title: <FormattedMessage {...messages.orders} />,
      value: dataIsUndefined ? doubleDash : <FormattedNumber value={totalOrderCount} />,
    },
    {
      title: <FormattedMessage {...messages.newCustomers} />,
      value: dataIsUndefined ? doubleDash : <FormattedNumber value={newCustomers} />,
    },
    {
      title: <FormattedMessage {...messages.returningCustomers} />,
      value: dataIsUndefined ? doubleDash : <FormattedNumber value={returnCustomers} />,
    },
  ];

  return <SummaryBar className={style['summary-bar']} data={formattedData} datumClassName={style['num-items-datum']} />;
};

const SalesTrends = ({ chartData = emptyObject }) => {
  const { orderByInterval, orderByStore, customerSummary, productByCategoryCount, productByAmount } = chartData;
  const defaultCurrency = useSelector(Global.selectSellerDefaultCurrency) || 'USD';

  return (
    <>
      {!customerSummary?.totalOrderCount && <InsufficientDataBannerMessage />}
      <div className={style['items-sold-over-time-graph']}>
        <NumItemsSoldOverTimeGraph data={orderByInterval} shouldShowNullOverlay={!customerSummary?.totalOrderCount} />
      </div>
      {renderSummarySection(customerSummary, defaultCurrency)}
      <div className={style['top-five-products-graph']}>
        <TopFiveProductsByRevenueTable data={productByAmount} />
        <p>
          <Link to='/dashboard/reports/ProductSales'>
            <FormattedMessage {...messages.productSalesDataLink} />
          </Link>
        </p>
      </div>
      <div className={style.container}>
        <div className={style['chart-section']}>
          <SalesByCategoryTable data={productByCategoryCount} />
        </div>
        <div className={style['chart-section']}>
          <SalesByStoreTable data={orderByStore} />
        </div>
      </div>
    </>
  );
};

SalesTrends.propTypes = {
  chartData: PropTypes.object,
};

export default SalesTrends;
